// Ported from [Nuxt](https://github.com/nuxt/nuxt/blob/main/packages/nuxt/src/app/composables/cookie.ts)

import type { CookieParseOptions, CookieSerializeOptions } from 'cookie-es'
import { parse, serialize } from 'cookie-es'
import { destr } from 'destr'
import cookiejs from 'cookiejs'

type _CookieOptions = Omit<CookieSerializeOptions & CookieParseOptions, 'decode' | 'encode'>

export interface CookieOptions<T = any> extends _CookieOptions {
  decode?(value: string): T
  encode?(value: T): string
  default?: () => T | Ref<T>
  watch?: boolean | 'shallow'
}

export type CookieRef<T> = Ref<T>

const CookieDefaults: CookieOptions<any> = {
  path: '/',
  watch: true,
  decode: val => destr(decodeURIComponent(val)),
  encode: val => encodeURIComponent(typeof val === 'string' ? val : JSON.stringify(val)),
}

export const useCookie = <T = string | null | undefined>(name: string, _opts?: CookieOptions<T>): CookieRef<T> => {
  const opts = { ...CookieDefaults, ..._opts || {} }
  const cookies = parse(document.cookie, opts)

  const cookie = ref<T | undefined>(cookies[name] as any ?? opts.default?.())


  watch(cookie, () => {
    serializeCookie(name, cookie.value, opts)
  })

  return cookie as CookieRef<T>
}

function serializeCookie(name: string, value: any, opts: CookieSerializeOptions = {}) {
  if (value === null || value === undefined) {
    cookiejs.remove(name)
    return serialize(name, value, {...opts, maxAge: -1})
  }
  cookiejs.set(name, JSON.stringify(value), { 'path': '/'})
  return serialize(name, value, opts)
}

// 簡單驗證 cookie 值是否安全
function isValidCookieValue(value: any): boolean {
  // 這裡可以根據具體的需求添加更嚴格的驗證
  // 比如確保值不是空字符串，也不是包含惡意字符的內容
  if (typeof value === 'string') {
    return /^[^;\r\n]*$/.test(value); // 確保沒有 `;`, `\r`, `\n` 等不允許的字符
  }
  return true; // 對於非字符串的情況，可以進行更多的檢查
}
